/**
 * Firm "act-as-client" store (first-class tenancy).
 *
 * Holds the short-lived act-as token minted by
 * POST /v1/firm/clients/{clientTenantId}/act-as. While a firm is "administering"
 * a client, the api() client sends this token (so the firm-user sees + acts on
 * the CLIENT's tenant), BUT the firm-user's own Supabase session stays intact —
 * the firm-console calls (the Client Book, onboarding, billing) must travel on
 * the operator's real session, so they pass `useOperatorSession: true`.
 *
 * This is the SAME mechanism as super_admin impersonation
 * (see impersonation-store.ts) — a per-session tenant switch — but it is the
 * firm's everyday work, not break-glass, so the banner that reads it is calm,
 * not alarming.
 *
 * sessionStorage-backed: survives an in-tab reload, never outlives the tab. The
 * 15-minute `exp` on the token is the hard backstop; the backend also re-checks
 * the grant fail-closed on every request, so a revoked grant ends the session on
 * its next call regardless of this token's TTL.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ActiveFirmAct {
  /** The act-as token (HS256, carries tenant_id = the client + actAs.firmTenantId). */
  jwt: string;
  /** The client tenant being administered. */
  clientTenantId: string;
  /** Human label for the context bar (the client's display name). */
  clientDisplay: string;
  /** What the firm may do on this client. Drives the read-only treatment. */
  accessLevel: 'read' | 'manage';
  /** ISO timestamp the act-as token expires. */
  expiresAt: string;
}

interface FirmActState {
  active: ActiveFirmAct | null;
  setFirmAct: (a: ActiveFirmAct) => void;
  clear: () => void;
}

export const useFirmActStore = create<FirmActState>()(
  persist(
    (set) => ({
      active: null,
      setFirmAct: (active) => set({ active }),
      clear: () => set({ active: null }),
    }),
    {
      name: 'elreign:firm-act:v1',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/**
 * Non-React accessor for the api() client. Returns the active act-as token when
 * present and unexpired, else null. Expired tokens are cleared so the context
 * bar drops and the client falls back to the firm-user's own session.
 */
export function activeFirmActToken(): string | null {
  const active = useFirmActStore.getState().active;
  if (!active) return null;
  if (Number.isNaN(Date.parse(active.expiresAt)) || Date.parse(active.expiresAt) <= Date.now()) {
    useFirmActStore.getState().clear();
    return null;
  }
  return active.jwt;
}

/**
 * Firm-act context status WITHOUT the clear-on-expiry side effect — the api()
 * client reads this to distinguish "never administering a client" (`none`) from
 * "was administering a client but the 15-min act-as token just expired mid-flow"
 * (`expired`). This matters because a data call under an EXPIRED act-as must NOT
 * silently fall back to the firm operator's own session — that would write the
 * CLIENT's data into the FIRM's tenant. The client throws + clears instead.
 */
export function firmActStatus(): 'none' | 'active' | 'expired' {
  const active = useFirmActStore.getState().active;
  if (!active) return 'none';
  const exp = Date.parse(active.expiresAt);
  if (Number.isNaN(exp) || exp <= Date.now()) return 'expired';
  return 'active';
}
